import { entries } from 'common_util_node/util';
import { ProductName, SalesChannel, BarcodeSegment } from './barcode';
import { KitOverviewResponse } from '../containers/Customers/component/Dial';
import { colors } from '../styles/colors';
import { Moment } from 'moment';
import { Ethnicity, Gender, PhoneType } from '../helpers/types';
import i18next from 'i18next';
import { MetaData } from './kit';

export type CustomerOverviewState = {
  loading: boolean;
  kitsData: {
    kits: Kit[];
    total: number;
  };
  profiles: Record<string, Profile>;
  kitStatusFilter: KitStatus | undefined;
  searchTerm: string;
  currentPage: number;
  kitOverViewResponse: KitOverviewResponse | undefined;
};

export type ReportFile = {
  ref: string;
  language: string;
};

export type Test = {
  datetime: string;
  extractedId: string;
  id: string;
  kitId: string;
  orderId: string;
  owner: string;
  primaryId: string;
  status: TestStatus;
  files: ReportFile[];
  testId: string;
  name: string;
  history: {
    status: string;
    datetime: string;
  }[];
};
export interface Status {
  id?: string;
  status: string;
  datetime: Moment;
}
export type KitData = {
  kitId: string;
  barcode: string;
  datetime: string;
  status: KitStatus;
};

export type Kit = {
  kitId: string;
  barcode: string;
  datetime: string;
  status: KitStatus;
  expectedReportReadyDate?: string;
  metadata: MetaData[];
  history?: KitHistoryItem[];
  lab?: {
    kitLabId?: string;
    labId?: string;
  };
  primary?: {
    datetime?: string;
    extracted?: [];
    history?: [];
    lab?: [];
    primarySampleId?: string;
    sampleId?: string;
    status?: string;
  }[];
  profile?: string | {
    kitProfileId?: string;
    profileId?: string;
    owner?: string;
  };
  test?: Test[];
  scope?: ProductName[],
  channel?: SalesChannel[];
  segment?: BarcodeSegment[];
  orderItems?: {
    kitOrderItemId?: string;
    orderItemId?: string;
  }[];
};

export const EMPTY_KIT: Kit = {
  kitId: '',
  barcode: '-',
  datetime: '',
  status: 'nokit',
  history: [],
  primary: [],
  profile: undefined,
  metadata: [],
};

export function getKitProfileId(kit: Kit): string | undefined {
  if (!kit.profile) {
    return undefined;
  }

  if (typeof kit.profile === 'string') {
    return kit.profile;
  }

  return kit.profile.profileId;
}

export type KitHistoryItem = {
  historyId: string;
  datetime: string;
  status: KitStatus;
  comment?: string;
};

export type KitStatus =
  | 'replaced'
  | 'ordered'
  | 'ready'
  | 'activated'
  | 'open'
  | 'rejected'
  | 'lab'
  | 'scheduled'
  | 'report-ready'
  | 'report-result-ready'
  | 'nokit' // exists on the frontend only - profiles without an activated kit
  ;

export const kitStatusDescriptions: Record<KitStatus, string> = {
  replaced: 'Replaced',
  ordered: 'Barcode Created', // created barcodes show up as "ordered" due to "enabler" orders
  ready: 'Sample At Lab',
  activated: `${i18next.t('cmp.overview.sampleAtLabNotRegistered')}`,
  open: 'Open (SKU not assigned)',
  rejected: `${i18next.t('cmp.overview.rejected')}`,
  lab: `${i18next.t('cmp.overview.sampleAtLabNotRegistered')}`,
  scheduled: 'Scheduled',
  'report-ready': 'report-ready',
  'report-result-ready': 'report-result-ready',
  nokit: 'No Kit',
};

export function isValidKitStatus(kitStatus: KitStatus | string): kitStatus is KitStatus {
  return kitStatus in kitStatusDescriptions;
}

export function getKitStatusDescription(status: KitStatus | string): string {
  return isValidKitStatus(status)
    ? kitStatusDescriptions[status]
    : status;
}

export function getKitStatusByDescription(statusDescription: string): KitStatus | undefined {
  const found = entries(kitStatusDescriptions)
    .find(([, description]) => description.toLowerCase() === statusDescription.toLowerCase());
  return found
    ? found[0]
    : undefined;
}

const kitStatusToColor: Partial<Record<KitStatus, string>> = {
  lab: colors.yellow,
  activated: colors.blue,
  ready: colors.azure,
  rejected: colors.red,
};

export function getKitStatusColor(status: KitStatus, reportReady: boolean | undefined): string {
  if (reportReady) {
    return colors.green;
  }

  return kitStatusToColor[status] || colors.primaryLight;
}

export type LatestKitsPayload = {
  kits: Kit[];
  total: string | number;
};
export type KitsPayload = {
  kits: KitData[];
  total: string | number;
};

export type TestStatus =
  | 'test-terminated'
  | 'report-result-ready'
  | 'test-created'
  | 'report-ready'
  | 'lab-result-source-approved'
  | 'lab-result-cnv-ready'
  | 'lab-result-source-ready'
  | 'lab-result-gt-approved'
  | 'lab-result-ready-approved'
  | 'score-result-ready'
  ;

export interface Profile {
  profileId: string;
  root?: true;
  datetime: string;
  address: string[];
  owner: string;
  phone: Phone[];
  email: {
    email: string;
    name?: string;
    primary: boolean;
  }[];
  health: {
    healthId: string;
    dob: string;
    ethnicity: Ethnicity;
    gender: Gender;
    height: string;
    weight: string;
    heightUnit: string;
    weightUnit: string;
  };
  name: {
    firstName: string;
    lastName: string;
    nickName?: string;
  };
  preference: {
    language: string;
  };
  tag: Tags[];
}
export interface Tags {
  tagId: string;
  tag: string;
  datetime: string;
  active: boolean;
}
export interface Phone {
  phoneId: string;
  datetime: string;
  primary: boolean;
  name: PhoneType;
  countryCode: string;
  number: string;
  mobile: boolean;
}